import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { getService } from "./service";

const initialState = {
  trainingParticipants: [],
  participantsByCourse: {},
  loading: false,
  error: null,
  total: 0,
};

const FETCH_REQUEST = "FETCH_PARTICIPANT_REQUEST";
const FETCH_SUCCESS = "FETCH_PARTICIPANT_SUCCESS";
const FETCH_FAILURE = "FETCH_PARTICIPANT_FAILURE";
const CREATE_REQUEST = "CREATE_PARTICIPANT_REQUEST";
const CREATE_SUCCESS = "CREATE_PARTICIPANT_SUCCESS";
const CREATE_FAILURE = "CREATE_PARTICIPANT_FAILURE";
const UPDATE_REQUEST = "UPDATE_PARTICIPANT_REQUEST";
const UPDATE_SUCCESS = "UPDATE_PARTICIPANT_SUCCESS";
const UPDATE_FAILURE = "UPDATE_PARTICIPANT_FAILURE";
const DELETE_REQUEST = "DELETE_PARTICIPANT_REQUEST";
const DELETE_SUCCESS = "DELETE_PARTICIPANT_SUCCESS";
const DELETE_FAILURE = "DELETE_PARTICIPANT_FAILURE";
const FETCH_BY_COURSE_REQUEST = "FETCH_BY_COURSE_REQUEST";
const FETCH_BY_COURSE_SUCCESS = "FETCH_BY_COURSE_SUCCESS";
const FETCH_BY_COURSE_FAILURE = "FETCH_BY_COURSE_FAILURE";

export const getTrainingParticipants =
  (
    page = 0,
    pageSize = 20,
    sortField = "id",
    sortDirection = "asc",
    filters = {},
    userAgency
  ) =>
  async (dispatch) => {
    const service = getService(serviceNames.perTraining);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      // lastName: { $like: '%Sharma%'}, // for searching in the fields
      isDeleted: false,
      agencyId: userAgency,
    };

    const textFields = [];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const dropdownFields = ["courseLocation", "employeeNum", "course"];

    dropdownFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (Array.isArray(filters.tagTypes) && filters.tagTypes.length > 0) {
      query.tagTypes = filters.tagTypes.map((t) => t.value);
    }

    if (filters.filterField?.value && filters.filterValue) {
      const fieldMap = {
        Hours: "hours",
        Category: "category",
        Comments: "comments",
        Grade: "grade",
      };

      const field =
        fieldMap[filters.filterField.value] || filters.filterField.value;

      query[field] = filters.exactMatch
        ? filters.filterValue
        : { $like: `%${filters.filterValue}%` };
    }

    if (filters.courseDateFrom) {
      query.courseDate = {
        ...(query.courseDate || {}),
        $gte: filters.courseDateFrom,
      };
    }

    if (filters.courseDateTo) {
      query.courseDate = {
        ...(query.courseDate || {}),
        $lte: filters.courseDateTo,
      };
    }

    if (filters.viewCoursesPendingRegistration) {
      query.dateRegistered = null;
    }

    if (filters.viewPendingCourses) {
      query.grade = null;
    }

    if (filters.approved) {
      query.approved = true;
    }

    if (filters.checkSent) {
      query.checkSent = true;
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_REQUEST,
        success: FETCH_SUCCESS,
        failure: FETCH_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => {
        const participantsByCourse = res.data.reduce((acc, participant) => {
          const courseNumber = participant.courseNumber || "Unknown";
          if (!acc[courseNumber]) {
            acc[courseNumber] = [];
          }
          acc[courseNumber].push(participant);
          return acc;
        }, {});

        return {
          trainingParticipants: res.data,
          total: res.total,
          participantsByCourse,
        };
      },
      errorMessagePrefix: "Failed to fetch training participants",
    });
  };

export const createTrainingParticipant =
  (trainingParticipantData) => async (dispatch) => {
    const service = getService(serviceNames.perTraining);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: CREATE_REQUEST,
        success: CREATE_SUCCESS,
        failure: CREATE_FAILURE,
      },
      apiCall: () => service.create(trainingParticipantData),
      successMessage: "Training Participant created successfully",
      errorMessagePrefix: "Failed to create training participant",
    });
  };

export const getParticipantsByCourseNumber =
  (courseNumber) => async (dispatch) => {
    const service = getService(serviceNames.perTraining);

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_BY_COURSE_REQUEST,
        success: FETCH_BY_COURSE_SUCCESS,
        failure: FETCH_BY_COURSE_FAILURE,
      },
      apiCall: () =>
        service.find({
          query: {
            courseNumber: courseNumber,
            isDeleted: false,
            $limit: -1,
          },
        }),
      transformResult: (res) => {
        return {
          courseNumber: courseNumber,
          participants: res || [],
        };
      },

      errorMessagePrefix: `Failed to fetch participants for course ${courseNumber}`,
    });
  };

export const updateTrainingParticipant =
  (id, trainingParticipantData, showSuccessMessage = true) =>
  async (dispatch) => {
    const service = getService(serviceNames.perTraining);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_REQUEST,
        success: UPDATE_SUCCESS,
        failure: UPDATE_FAILURE,
      },
      apiCall: () => service.patch(id, trainingParticipantData),
      successMessage: showSuccessMessage
        ? "Training Participant updated successfully"
        : null,
      errorMessagePrefix: "Failed to update training participant",
    });
  };

export const deleteTrainingParticipant = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.perTraining);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_REQUEST,
      success: DELETE_SUCCESS,
      failure: DELETE_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Training Participant deleted successfully",
    errorMessagePrefix: "Failed to delete training participant",
  });
};

export default function trainingParticipantReducer(
  state = initialState,
  action
) {
  switch (action.type) {
    case FETCH_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case DELETE_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingParticipants: action.payload.trainingParticipants,
        total: action.payload.total,
        participantsByCourse: action.payload.participantsByCourse,
      };
    case CREATE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingParticipants: [...state.trainingParticipants, action.payload],
        total: state.total + 1,
      };
    case UPDATE_SUCCESS: {
      const updatedParticipant = action.payload;

      const updatedTPs = Array.isArray(state.trainingParticipants)
        ? state.trainingParticipants.map((tp) =>
            tp.id === updatedParticipant.id ? updatedParticipant : tp
          )
        : [];

      const updatedParticipantsByCourse = { ...state.participantsByCourse };

      Object.keys(updatedParticipantsByCourse).forEach((courseNumber) => {
        const participants = updatedParticipantsByCourse[courseNumber];
        if (Array.isArray(participants)) {
          updatedParticipantsByCourse[courseNumber] = participants.map((p) =>
            p.id === updatedParticipant.id ? updatedParticipant : p
          );
        }
      });

      return {
        ...state,
        loading: false,
        trainingParticipants: updatedTPs,
        participantsByCourse: updatedParticipantsByCourse,
      };
    }

    case DELETE_SUCCESS: {
      const deletedId = action.payload;

      const updatedTPs = state.trainingParticipants.filter(
        (tp) => tp.id !== deletedId
      );

      const updatedParticipantsByCourse = { ...state.participantsByCourse };
      Object.keys(updatedParticipantsByCourse).forEach((courseNumber) => {
        updatedParticipantsByCourse[courseNumber] = updatedParticipantsByCourse[
          courseNumber
        ].filter((p) => p.id !== deletedId);
      });

      return {
        ...state,
        loading: false,
        trainingParticipants: updatedTPs,
        participantsByCourse: updatedParticipantsByCourse,
        total: state.total - 1,
      };
    }
    case FETCH_BY_COURSE_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_BY_COURSE_SUCCESS:
      return {
        ...state,
        loading: false,
        participantsByCourse: {
          ...state.participantsByCourse,
          [action.payload.courseNumber]: action.payload.participants,
        },
      };

    case FETCH_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case DELETE_FAILURE:
    case FETCH_BY_COURSE_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
